import React, { Component } from 'react'
import PropTypes from 'prop-types'
import {
  get,
  union,
  merge,
  pickBy,
  filter,
  matches,
  isString,
  isFunction,
  sortedIndexBy,
} from 'lodash'
import { Link } from 'react-router-dom'

import { PageWrapper } from 'Components/Layout/Page/PageWrapper'
import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { FilterBox } from 'Components/Filter/FiltersWrapper/box'
import { FilterSelect } from 'Components/Filter/FilterSelect'
import { FiltersWrapper } from 'Components/Filter/FiltersWrapper'
import { SearchInput } from 'Components/Header/SearchInput'
import { Table } from 'Components/Table'
import { Td } from 'Components/Table/Td'
import { Th } from 'Components/Table/Th'
import { Tr } from 'Components/Table/Tr'
import { ButtonAdd } from 'Components/Buttons/ButtonAdd'

export default class FilteredTable extends Component {
  state = {
    searchTerm: null,
    filters: {},
    sortColumn: null,
    sortReverse: false,
    normalizedData: [],
    filterOptions: {},
  }

  componentDidMount() {
    this.setState(() => ({
      ...this.normalizeData(
        this.props.data,
        this.props.columns,
        this.props.keyExtractor,
        this.props.filters,
      ),
      ...this.setDefaultValues(),
    }))
  }

  componentWillReceiveProps(newProps) {
    this.setState(() => ({
      ...this.normalizeData(
        newProps.data,
        newProps.columns,
        newProps.keyExtractor,
        newProps.filters,
      ),
      ...this.setDefaultValues(),
      orderBy: null,
      sortBy: null,
      searchTerm: null,
    }))
  }

  render() {
    return (
      <PageWrapper>
        <LayoutDashboard>
          {this.props.filterComponent && this.props.filterComponent}
          {!this.props.filterComponent && this.renderFilters()}
        </LayoutDashboard>
        <Table>
          {this.renderTableHead()}
          {this.renderTableBody()}
        </Table>
      </PageWrapper>
    )
  }

  setDefaultValues = () =>
    filter(this.props.filters, 'defaultValue')
      .map(({ columnName, defaultValue }) => this.setFilters(defaultValue)())
      .reduce(merge, null)

  // TODO: filter with term based on all fields in columns
  searchHandler = searchTerm => {
    this.setState(
      state => {
        return {
          ...state,
          searchTerm,
        }
      },
      () => this.props.onSearch(this.state.searchTerm),
    )
  }

  changeSort = columnName => {
    this.setState(state => {
      return {
        ...state,
        sortColumn: columnName,
        sortReverse: state.sortColumn === columnName && !state.sortReverse,
      }
    })
  }

  getFilterValue = (name, index) => {
    var val = this.state.filterOptions[name][index]
    if (val && typeof val === 'object') return val.value
    return val
  }

  setFilters = defaultValue => ({ name, value } = {}) => ({
    ...this.state,
    filters: {
      ...this.state.filters,
      [name]: defaultValue || value,
    },
  })

  // TODO: just set the filter on state, creating a object with all selected filters
  changeFilter = name => (evt, index, value) => {
    if (this.props.onFilterValueChange) {
      this.props.onFilterValueChange(name, value)
    }
    const newState = this.setFilters()({ name, value })
    this.setState(newState)
  }

  filterData = (items, filters) => {
    const matchFilter = matches(pickBy(filters, f => f))
    const getIndex = (arr, item) => {
      if (!this.state.sortColumn) return arr.length

      return sortedIndexBy(arr, item, this.state.sortColumn)
    }

    const searchByTerm = item => {
      if (this.state.searchTerm) {
        return Object.values(item).find(v => {
          return (
            isString(v) &&
            v.toUpperCase().indexOf(this.state.searchTerm.toUpperCase()) > -1
          )
        })
      }
      return true
    }

    const filteredData = items.reduce((result, item) => {
      if (searchByTerm(item) && matchFilter(item)) {
        const newIndex = getIndex(result, item)
        result.splice(newIndex, 0, item)
      }
      return result
    }, [])

    return this.state.sortReverse ? filteredData.reverse() : filteredData
  }

  normalizeData = (items, columns, keyExtractor, filters) => {
    const getAcessor = column => {
      return isFunction(column.accessor)
        ? column.accessor
        : item => get(item, column.accessor)
    }

    const normalizeItem = item => {
      return columns.reduce(
        (resultItem, column) => {
          return {
            ...resultItem,
            [column.name]: getAcessor(column)(item),
          }
        },
        { key: keyExtractor(item) },
      )
    }

    const filterOptions = {}

    let filtersWithNoOptionsDefined = []

    var stateFilters = this.state.filters
    filters.forEach(filter => {
      if (filter.options) {
        filterOptions[filter.columnName] = [null, ...filter.options]
      } else {
        filtersWithNoOptionsDefined.push(filter)
      }
      if (filter.value !== undefined) {
        stateFilters = {
          ...stateFilters,
          [filter.columnName]: filter.value,
        }
      }
    })
    this.setState({ filters: stateFilters })

    const normalizedData = items.reduce((result, item) => {
      const nItem = normalizeItem(item)

      Object.keys(nItem).map(columnName => {
        const baseFilter = filtersWithNoOptionsDefined.find(
          f => f.columnName === columnName,
        )

        if (baseFilter && nItem[columnName]) {
          filterOptions[columnName] = union(
            filterOptions[columnName] || [null],
            [nItem[columnName]],
          )
        }

        return true
      })

      result.push(nItem)
      return result
    }, [])

    return {
      normalizedData,
      filterOptions,
    }
  }

  renderFilters = () => {
    const {
      filters,
      onButtonAddHandler,
      showSearchFilter,
      searchLabel,
    } = this.props

    const dropdownFilters = filters.map(filter => {
      const options = (this.state.filterOptions[filter.columnName] || []).map(
        value => {
          if (value && typeof value === 'object') {
            var label = value.label
            var val = value.value
          }

          return {
            value: val || value,
            label: label || value || filter.noValueLabel,
          }
        },
      )
      return (
        <FilterSelect
          key={`select-${filter.columnName}`}
          className={`select-${filter.columnName}`}
          hasPrimaryText={false}
          label={filter.label}
          onChange={this.changeFilter(filter.columnName)}
          options={options}
          value={this.state.filters[filter.columnName] || null}
          autoWidth={options.length > 0 ? filter.autoWidth : false}
          sorted={filter.sorted}
          ASC={filter.ASC}
        />
      )
    })

    return (
      <FiltersWrapper className="filters-wrapper">
        <FilterBox className="filter-box">
          {showSearchFilter && (
            <SearchInput
              className="search-input"
              placeholder={searchLabel}
              searchHandler={this.searchHandler}
              value={this.state.searchTerm}
            />
          )}
        </FilterBox>
        <FilterBox dropdowns className="filter-box">
          {dropdownFilters}
          {onButtonAddHandler && (
            <ButtonAdd
              className="button-add"
              onClickHandler={onButtonAddHandler}
            />
          )}
        </FilterBox>
      </FiltersWrapper>
    )
  }

  renderTableHead = () => {
    const tableHeadColumns = this.props.columns.map(column => {
      const cellProps = {
        className: `column-${column.name} ${column.class || ''}`,
        key: column.name,
      }

      if (column.sortable) {
        cellProps.className = `sortable ${cellProps.className}`
        cellProps.onClick = () => this.changeSort(column.name)
      }
      return <Th {...cellProps}>{column.label}</Th>
    })

    return (
      <thead>
        <Tr>{tableHeadColumns}</Tr>
      </thead>
    )
  }

  renderTableBody = () => {
    const filteredData = this.filterData(
      this.state.normalizedData,
      this.state.filters,
    )
    const rowsJSX = filteredData.map(row => {
      return this.renderTableRow(row)
    })

    return (
      <tbody>
        {rowsJSX}
        {this.getFooterRows()}
      </tbody>
    )
  }

  renderTableRow = row => {
    const TdWrapper = ({ isAction, children }) =>
      this.props.redirectNamespace && !isAction ? (
        <Link
          className="invisible-link"
          to={`${this.props.redirectNamespace}/${row.key}`}
        >
          {children}
        </Link>
      ) : (
        children
      )

    const cellsJSX = this.props.columns.map((column, index) => {
      const key = `${row.key}-${index}`
      const value = column.render
        ? column.render(row[column.name], row)
        : row[column.name]
      return (
        <Td
          key={key}
          actions={column.type === 'actions'}
          className={`column-${column.name} ${column.class || ''}`}
        >
          <TdWrapper isAction={column.type === 'actions'}>
            {value || ''}
          </TdWrapper>
        </Td>
      )
    })

    return <Tr key={row.key}>{cellsJSX}</Tr>
  }

  getFooterRows = () => {
    const { footerRows } = this.props
    return footerRows
  }
}

FilteredTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      accessor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.func,
      ]).isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
    }),
  ).isRequired,
  keyExtractor: PropTypes.func,
  filterComponent: PropTypes.node,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      noValueLabel: PropTypes.string,
      columnName: PropTypes.string,
      options: PropTypes.array,
    }),
  ),
  createRoute: PropTypes.string,
  onSearch: PropTypes.func,
  searchLabel: PropTypes.string,
  showSearchFilter: PropTypes.bool,
  onButtonAddHandler: PropTypes.func,
}

FilteredTable.defaultProps = {
  filterComponent: null,
  keyExtractor: item => item.id,
  filters: [],
  onSearch: term => console.warn('onSearch not implemented', term),
  searchLabel: 'Search anything',
  showSearchFilter: true,
}



// WEBPACK FOOTER //
// ./src/Containers/FilteredTable/index.js